// Log or edit a trade: symbol, side, quantity, entry/exit (price and time), charges, the planned
// stop and target (for R multiples), setup, habits and a note. Logged from a stock page, it also
// keeps a snapshot of what MoneyHQ showed at that moment.
import { useEffect, useRef, useState } from 'react';
import { CheckIcon } from 'lucide-react';
import { NSE } from '@planb/core';
import { cn } from '@/lib/utils';
import { SETUPS, TAGS, type PlanSnapshot, type Trade, type TradeInput } from '@/lib/client/use-trades';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Pills } from './market-ui';

const cur = NSE.currencySymbol;
/** <input type="datetime-local"> value for an ISO time, in this browser's time zone. */
const local = (iso: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
};
const num = (s: string) => (s.trim() === '' ? null : Number(s));

export interface TradeDraft { symbol?: string; side?: 'long' | 'short'; entry?: number; stop?: number | null; target?: number | null; setup?: string; plan?: PlanSnapshot | null }

export function TradeForm({ open, onOpenChange, trade, draft, onSave }: {
  open: boolean; onOpenChange: (o: boolean) => void;
  /** Editing an existing trade, or a new one from a draft (stock page). */
  trade?: Trade | null; draft?: TradeDraft;
  onSave: (t: TradeInput) => Promise<string | null>;
}) {
  const [f, setF] = useState(() => blank());
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function blank() {
    return { symbol: '', segment: 'EQ' as 'EQ' | 'FO', side: 'long' as 'long' | 'short', qty: '', entry: '', entryAt: local(new Date().toISOString()), exit: '', exitAt: '', fees: '', stop: '', target: '', setup: '', tags: [] as string[], note: '', plan: null as PlanSnapshot | null };
  }
  // Filled once as the dialog opens (or for a different trade), then left to the user: on a stock page
  // the draft is rebuilt on every live tick, and refilling from it wiped what was being typed. The
  // plan snapshot is MoneyHQ's plan at the moment the dialog opened.
  const filledFor = useRef<string | null>(null);
  useEffect(() => {
    if (!open) { filledFor.current = null; return; }
    const key = trade ? `trade:${trade.id}` : 'new';
    if (filledFor.current === key) return;
    filledFor.current = key;
    setError(null);
    if (trade) {
      setF({ symbol: trade.symbol, segment: trade.segment, side: trade.side, qty: String(trade.qty), entry: String(trade.entry), entryAt: local(trade.entryAt),
        exit: trade.exit == null ? '' : String(trade.exit), exitAt: local(trade.exitAt), fees: trade.fees ? String(trade.fees) : '',
        stop: trade.stop == null ? '' : String(trade.stop), target: trade.target == null ? '' : String(trade.target), setup: trade.setup, tags: trade.tags, note: trade.note, plan: trade.plan });
    } else {
      const b = blank();
      setF({ ...b, symbol: draft?.symbol ?? '', side: draft?.side ?? 'long', entry: draft?.entry ? draft.entry.toFixed(2) : '',
        stop: draft?.stop ? draft.stop.toFixed(2) : '', target: draft?.target ? draft.target.toFixed(2) : '', setup: draft?.setup ?? '', plan: draft?.plan ?? null });
    }
  }, [open, trade, draft]);

  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setF((x) => ({ ...x, [k]: e.target.value }));
  const toggleTag = (id: string) => setF((x) => ({ ...x, tags: x.tags.includes(id) ? x.tags.filter((t) => t !== id) : [...x.tags, id] }));

  // Live preview of the result and its R multiple.
  const q = Number(f.qty), en = Number(f.entry), ex = num(f.exit), st = num(f.stop), dir = f.side === 'short' ? -1 : 1;
  const net = ex != null && q > 0 && en > 0 ? (ex - en) * q * dir - (num(f.fees) ?? 0) : null;
  const risk = st != null && en > 0 ? (en - st) * dir : null;
  const r = net != null && risk && risk > 0 && q > 0 ? net / (risk * q) : null;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    const exitAt = f.exit.trim() ? (f.exitAt ? new Date(f.exitAt).toISOString() : new Date().toISOString()) : null;
    const err = await onSave({
      symbol: f.symbol.trim().toUpperCase(), segment: f.segment, side: f.side, qty: Math.round(q), entry: en,
      exit: num(f.exit), entryAt: f.entryAt ? new Date(f.entryAt).toISOString() : '', exitAt, fees: num(f.fees) ?? 0,
      stop: num(f.stop), target: num(f.target), setup: f.setup, tags: f.tags, note: f.note, plan: f.plan,
    });
    setBusy(false);
    if (err) setError(err); else onOpenChange(false);
  }

  const field = (id: string, label: string, el: React.ReactNode, hint?: string) => (
    <div className="grid gap-1.5">
      <Label htmlFor={id} className="text-[12.5px]">{label}{hint && <span className="font-normal text-muted-foreground"> · {hint}</span>}</Label>
      {el}
    </div>
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{trade ? 'Edit trade' : 'Log a trade'}</DialogTitle>
          <DialogDescription>{f.plan ? 'MoneyHQ’s plan at this moment is saved with the trade, so you can compare plan and result later.' : 'Leave the exit empty while the position is open.'}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid gap-4">
          <div className="grid gap-3 sm:grid-cols-[1fr_auto_auto]">
            {field('t-sym', 'Symbol', <Input id="t-sym" required value={f.symbol} onChange={set('symbol')} placeholder="TCS" className="uppercase" autoFocus={!f.symbol} />)}
            <div className="grid gap-1.5"><span className="text-[12.5px] font-medium">Side</span>
              <Pills size="sm" label="Side" value={f.side} onChange={(v) => setF((x) => ({ ...x, side: v }))} options={[{ id: 'long', label: 'Long' }, { id: 'short', label: 'Short' }]} /></div>
            <div className="grid gap-1.5"><span className="text-[12.5px] font-medium">Segment</span>
              <Pills size="sm" label="Segment" value={f.segment} onChange={(v) => setF((x) => ({ ...x, segment: v }))} options={[{ id: 'EQ', label: 'Equity' }, { id: 'FO', label: 'F&O' }]} /></div>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            {field('t-qty', 'Quantity', <Input id="t-qty" required inputMode="numeric" value={f.qty} onChange={set('qty')} className="num" />)}
            {field('t-entry', `Entry ${cur}`, <Input id="t-entry" required inputMode="decimal" value={f.entry} onChange={set('entry')} className="num" />)}
            {field('t-entry-at', 'Entry time', <Input id="t-entry-at" required type="datetime-local" value={f.entryAt} onChange={set('entryAt')} />)}
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            {field('t-fees', `Charges ${cur}`, <Input id="t-fees" inputMode="decimal" value={f.fees} onChange={set('fees')} placeholder="0" className="num" />, 'brokerage + taxes')}
            {field('t-exit', `Exit ${cur}`, <Input id="t-exit" inputMode="decimal" value={f.exit} onChange={set('exit')} placeholder="open" className="num" />)}
            {field('t-exit-at', 'Exit time', <Input id="t-exit-at" type="datetime-local" value={f.exitAt} onChange={set('exitAt')} disabled={!f.exit.trim()} />)}
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            {field('t-stop', `Planned stop ${cur}`, <Input id="t-stop" inputMode="decimal" value={f.stop} onChange={set('stop')} className="num" />, 'for R')}
            {field('t-target', `Planned target ${cur}`, <Input id="t-target" inputMode="decimal" value={f.target} onChange={set('target')} className="num" />)}
            <div className="grid gap-1.5"><span className="text-[12.5px] font-medium">Setup</span>
              <Select value={f.setup || 'none'} onValueChange={(v) => setF((x) => ({ ...x, setup: v === 'none' ? '' : v }))}>
                <SelectTrigger className="h-9 w-full" aria-label="Setup"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="none">No setup</SelectItem>{SETUPS.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <div className="grid gap-1.5">
            <span className="text-[12.5px] font-medium">How it went <span className="font-normal text-muted-foreground">· habits and mistakes</span></span>
            <div className="flex flex-wrap gap-1.5">
              {TAGS.map((t) => {
                const on = f.tags.includes(t.id);
                return (
                  <button key={t.id} type="button" aria-pressed={on} onClick={() => toggleTag(t.id)}
                    className={cn('inline-flex h-7 items-center gap-1 rounded-full border px-2.5 text-xs font-medium transition-colors',
                      on ? (t.good ? 'border-bull bg-bull-soft text-bull-strong' : 'border-bear bg-bear-soft text-bear-strong') : 'bg-card text-muted-foreground hover:text-ink')}>
                    {on && <CheckIcon className="size-3" />}{t.label}
                  </button>
                );
              })}
            </div>
          </div>
          {field('t-note', 'Note', <Textarea id="t-note" rows={3} value={f.note} onChange={set('note')} placeholder="Why you took it, what you'd do differently…" />)}
          {f.plan && (
            <div className="rounded-lg border bg-tint px-3 py-2 text-[12px] text-muted-foreground">
              <b className="text-ink">MoneyHQ at {f.plan.time ?? 'entry'}:</b>{' '}
              {f.plan.z != null ? `${f.plan.zone ?? 'zone'} · Z ${cur}${f.plan.z.toFixed(2)}, stop ${cur}${f.plan.stop?.toFixed(2)}, T1 ${cur}${f.plan.t1?.toFixed(2)}, R:R ${f.plan.rr == null ? '—' : '1:' + f.plan.rr.toFixed(1)}` : 'no active zone'}
              {f.plan.score != null && ` · strength ${f.plan.score}`}{f.plan.eventRisk ? ` · event risk` : ''}
            </div>
          )}
          {error && <p role="alert" className="rounded-lg border border-bear/30 bg-bear-soft px-3 py-2 text-sm text-ink">{error}</p>}
          <DialogFooter className="items-center sm:justify-between">
            <span className={cn('num text-sm font-medium', net == null ? 'text-muted-foreground' : net >= 0 ? 'text-bull' : 'text-bear')}>
              {net == null ? 'Open position' : `${net >= 0 ? '+' : '−'}${cur}${Math.abs(net).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`}
              {r != null && ` · ${r >= 0 ? '+' : ''}${r.toFixed(2)}R`}
            </span>
            <span className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button type="submit" disabled={busy}>{trade ? 'Save' : 'Log trade'}</Button>
            </span>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
